import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import "../AdminDashboard/AdminDashboard.css";
import "./GymAssets.css";
import { ASSET_CATEGORIES, ASSET_CONDITIONS, ASSET_STATUSES, conditionLabel, money } from "./gymAssetOptions";

const emptyForm = () => ({
  branch_id: "",
  name: "",
  category: "Gym Equipment",
  subcategory: "",
  location: "",
  department: "",
  quantity: "1",
  unit_price: "",
  purchase_date: "",
  brand: "",
  model: "",
  serial_number: "",
  supplier: "",
  invoice_number: "",
  payment_method: "",
  warranty_start: "",
  warranty_end: "",
  useful_life_years: "",
  depreciation_method: "none",
  installation_date: "",
  custodian: "",
  condition: "good",
  status: "active",
  remarks: "",
  description: "",
});

export default function GymAssetForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm());
  const [branches, setBranches] = useState([]);
  const [serials, setSerials] = useState([""]);
  const [more, setMore] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    UserServices.branchNameAll()
      .then((res) => setBranches(res.data?.data || []))
      .catch(() => setBranches([]));
    if (!id) return;
    UserServices.gymAsset(id)
      .then((res) => {
        const row = res.data?.data || {};
        setForm({
          ...emptyForm(),
          ...row,
          quantity: String(row.quantity ?? 1),
          unit_price: row.unit_price ?? "",
          useful_life_years: row.useful_life_years ?? "",
        });
        setMore(Boolean(
          row.subcategory || row.department || row.brand || row.model || row.supplier
          || row.invoice_number || row.payment_method || row.purchase_date
          || row.warranty_start || row.warranty_end || row.useful_life_years
          || (row.depreciation_method && row.depreciation_method !== "none")
          || row.installation_date || row.custodian
          || (row.condition && row.condition !== "good")
          || (row.status && row.status !== "active")
          || row.remarks
        ));
      })
      .catch(() => toast.error("Could not open this asset"));
  }, [id]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const filledSerials = serials.map((serial) => serial.trim()).filter(Boolean);
  const several = !id && filledSerials.length > 0;
  const unitCount = several ? filledSerials.length : Math.max(1, Number(form.quantity) || 0);
  const total = unitCount * Math.max(0, Number(form.unit_price) || 0);

  const save = async (addAnother) => {
    if (!form.name.trim() || !form.branch_id || !form.category) {
      toast.error("Name, branch, and category are required");
      return;
    }
    const payload = {
      ...form,
      name: form.name.trim(),
      quantity: several ? 1 : Number(form.quantity) || 1,
      unit_price: form.unit_price === "" ? null : Number(form.unit_price),
      useful_life_years: form.useful_life_years === "" ? null : Number(form.useful_life_years),
      purchase_date: form.purchase_date || null,
      warranty_start: form.warranty_start || null,
      warranty_end: form.warranty_end || null,
      installation_date: form.installation_date || null,
      serial_numbers: several ? filledSerials : undefined,
    };
    setSaving(true);
    try {
      const res = id
        ? await UserServices.updateGymAsset(id, payload)
        : await UserServices.createGymAsset(payload);
      const body = res.data?.data || {};
      toast.success(id ? "Asset updated" : body.count > 1 ? `Saved ${body.count} assets` : "Asset saved");
      if (addAnother && !id) {
        setForm((current) => ({ ...current, name: "", serial_number: "" }));
        setSerials([""]);
      } else if (body.count > 1) {
        navigate("/gym-assets");
      } else {
        navigate("/gym-assets/" + (body.id || body.assets?.[0]?.id));
      }
    } catch (err) {
      const message = err.response?.data?.message
        || Object.values(err.response?.data?.errors || {})?.[0]?.[0]
        || "Could not save the asset";
      toast.error(typeof message === "string" ? message : "Could not save the asset");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wrapper gym-assets">
      <ToastContainer />
      <main className="page-content customer-contnent">
        <div className="site-main-design ad-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="ad-topbar d-flex justify-content-between align-items-end gap-3">
              <div>
                <p className="ad-eyebrow"><Link to="/gym-assets">Asset Management</Link></p>
                <h2 className="ad-title">{id ? "Edit asset" : "Add asset"}</h2>
                <p className="ad-subtitle">Name, branch, and category are required. The rest can be filled in later.</p>
              </div>
              <div className="gym-actions">
                <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={() => navigate("/gym-assets")}>
                  Cancel
                </button>
                {!id ? (
                  <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={() => save(true)}>
                    Save and add another
                  </button>
                ) : null}
                <button type="button" className="btn btn-danger" disabled={saving} onClick={() => save(false)}>
                  {saving ? "Saving..." : "Save asset"}
                </button>
              </div>
            </div>

            <div className="gym-panel p-4">
              <section className="gym-section">
                <h3>Asset</h3>
                <div className="gym-form-grid">
                  <Field label="Asset name">
                    <input className="form-control" value={form.name} onChange={(e) => set("name", e.target.value)} />
                  </Field>
                  <Field label="Branch">
                    <select className="form-select" value={form.branch_id} disabled={!!id} onChange={(e) => set("branch_id", e.target.value)}>
                      <option value="">Select branch</option>
                      {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Category">
                    <select className="form-select" value={form.category} onChange={(e) => set("category", e.target.value)}>
                      {ASSET_CATEGORIES.map((category) => <option key={category}>{category}</option>)}
                    </select>
                  </Field>
                  <Field label="Location">
                    <input className="form-control" value={form.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="Cardio area, reception, office" />
                  </Field>
                  <Field label="Unit price (PKR)">
                    <input className="form-control" type="number" min="0" value={form.unit_price} onChange={(e) => set("unit_price", e.target.value)} />
                  </Field>
                  <div className="gym-total">
                    <span>{several ? "Total for these serials" : "Total purchase cost"}</span>
                    <strong>{money(total)}</strong>
                  </div>
                  {id ? (
                    <Field label="Serial number">
                      <input className="form-control" value={form.serial_number || ""} onChange={(e) => set("serial_number", e.target.value)} />
                    </Field>
                  ) : (
                    <div className="gym-field gym-span-2">
                      <span>Serial numbers</span>
                      <p className="gym-muted mb-2">Each serial is saved as its own asset. Leave this empty to save one asset with a quantity.</p>
                      <div className="gym-serials">
                        {serials.map((serial, index) => (
                          <div className="gym-serial-row" key={index}>
                            <input
                              className="form-control"
                              value={serial}
                              placeholder={`Serial ${index + 1}`}
                              onChange={(event) => setSerials((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))}
                            />
                            {serials.length > 1 ? (
                              <button type="button" className="btn btn-outline-secondary" onClick={() => setSerials((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
                                Remove
                              </button>
                            ) : null}
                          </div>
                        ))}
                      </div>
                      <button type="button" className="btn btn-link px-0" onClick={() => setSerials((current) => [...current, ""])}>
                        Add another serial
                      </button>
                    </div>
                  )}
                  {!several ? (
                    <Field label="Quantity">
                      <input className="form-control" type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} />
                    </Field>
                  ) : (
                    <div className="gym-total">
                      <span>Assets to create</span>
                      <strong>{filledSerials.length}</strong>
                    </div>
                  )}
                </div>
              </section>

              <button type="button" className="btn btn-link px-0 mt-2" onClick={() => setMore((open) => !open)}>
                {more ? "Hide additional details" : "Add additional details"}
              </button>

              {more ? (
                <section className="gym-section">
                  <h3>Additional details</h3>
                  <div className="gym-form-grid">
                    <Field label="Subcategory">
                      <input className="form-control" value={form.subcategory || ""} onChange={(e) => set("subcategory", e.target.value)} placeholder="Treadmill, chair, laptop" />
                    </Field>
                    <Field label="Department">
                      <input className="form-control" value={form.department || ""} onChange={(e) => set("department", e.target.value)} />
                    </Field>
                    <Field label="Brand">
                      <input className="form-control" value={form.brand || ""} onChange={(e) => set("brand", e.target.value)} />
                    </Field>
                    <Field label="Model">
                      <input className="form-control" value={form.model || ""} onChange={(e) => set("model", e.target.value)} />
                    </Field>
                    <Field label="Supplier">
                      <input className="form-control" value={form.supplier || ""} onChange={(e) => set("supplier", e.target.value)} />
                    </Field>
                    <Field label="Invoice number">
                      <input className="form-control" value={form.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} />
                    </Field>
                    <Field label="Payment method">
                      <select className="form-select" value={form.payment_method || ""} onChange={(e) => set("payment_method", e.target.value)}>
                        <option value="">Not set</option>
                        <option>Cash</option>
                        <option>Bank</option>
                        <option>Cheque</option>
                      </select>
                    </Field>
                    <Field label="Purchase date">
                      <input className="form-control" type="date" value={form.purchase_date || ""} onChange={(e) => set("purchase_date", e.target.value)} />
                    </Field>
                    <Field label="Custodian">
                      <input className="form-control" value={form.custodian || ""} onChange={(e) => set("custodian", e.target.value)} />
                    </Field>
                    <Field label="Condition">
                      <select className="form-select" value={form.condition} onChange={(e) => set("condition", e.target.value)}>
                        {ASSET_CONDITIONS.map((condition) => <option key={condition} value={condition}>{conditionLabel(condition)}</option>)}
                      </select>
                    </Field>
                    <Field label="Status">
                      <select className="form-select" value={form.status} onChange={(e) => set("status", e.target.value)}>
                        {ASSET_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </Field>
                    <Field label="Warranty start">
                      <input className="form-control" type="date" value={form.warranty_start || ""} onChange={(e) => set("warranty_start", e.target.value)} />
                    </Field>
                    <Field label="Warranty end">
                      <input className="form-control" type="date" value={form.warranty_end || ""} onChange={(e) => set("warranty_end", e.target.value)} />
                    </Field>
                    <Field label="Useful life (years)">
                      <input className="form-control" type="number" min="0" value={form.useful_life_years} onChange={(e) => set("useful_life_years", e.target.value)} />
                    </Field>
                    <Field label="Depreciation">
                      <select className="form-select" value={form.depreciation_method || "none"} onChange={(e) => set("depreciation_method", e.target.value)}>
                        <option value="none">None</option>
                        <option value="straight_line">Straight line</option>
                      </select>
                    </Field>
                    <Field label="Installation date">
                      <input className="form-control" type="date" value={form.installation_date || ""} onChange={(e) => set("installation_date", e.target.value)} />
                    </Field>
                    <Field label="Remarks" wide>
                      <textarea className="form-control" rows="3" value={form.remarks || ""} onChange={(e) => set("remarks", e.target.value)} />
                    </Field>
                  </div>
                </section>
              ) : null}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <label className={wide ? "gym-field gym-span-2" : "gym-field"}>
      <span>{label}</span>
      {children}
    </label>
  );
}
